import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Star, Trash2, Plus, Check, X } from "lucide-react";

export function Stars({ n = 5 }) {
  return <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`w-4 h-4 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</div>;
}

const STATUS_CLS = { pending: "bg-amber-100 text-amber-800 border-amber-300", approved: "bg-emerald-100 text-emerald-800 border-emerald-300", rejected: "bg-rose-100 text-rose-800 border-rose-300" };

export default function TestimonialsAdmin() {
  const { t } = useApp();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ name: "", city: "", text: "", rating: 5 });
  const load = () => api.get("/testimonials").then((r) => setRows(r.data.items));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.name || !f.text) return toast.error(t("fill_required"));
    try { await api.post("/testimonials", f); toast.success(t("saved")); setF({ name: "", city: "", text: "", rating: 5 }); load(); } catch { toast.error(t("failed")); }
  };
  const setStatus = async (id, status) => { try { await api.patch(`/testimonials/${id}/status`, { status }); load(); } catch { toast.error(t("failed")); } };
  const del = async (id) => { if (window.confirm(t("delete") + "?")) { await api.delete(`/testimonials/${id}`); load(); } };
  const pending = rows.filter((r) => r.status === "pending");
  const others = rows.filter((r) => r.status !== "pending");
  const Row = ({ r }) => (
    <Card key={r.id} data-testid={`tst-row-${r.id}`}><CardContent className="p-4 flex gap-3">
      <div className="flex-1"><div className="flex flex-wrap items-center gap-2"><b>{r.name}</b><span className="text-xs text-slate-500">{r.city}</span><Stars n={r.rating} />
        <Badge className={STATUS_CLS[r.status] || ""} data-testid={`tst-status-${r.id}`}>{t(`tst_${r.status}`)}</Badge>
        {r.booking_code && <span className="text-xs font-mono text-slate-400">{r.booking_code}</span>}</div>
        <p className="text-sm text-slate-600 mt-1">{r.text}</p></div>
      <div className="flex flex-col gap-1">
        {r.status !== "approved" && <Button size="sm" variant="outline" className="text-emerald-700" onClick={() => setStatus(r.id, "approved")} data-testid={`tst-approve-${r.id}`}><Check className="w-4 h-4" /></Button>}
        {r.status !== "rejected" && <Button size="sm" variant="outline" className="text-amber-700" onClick={() => setStatus(r.id, "rejected")} data-testid={`tst-reject-${r.id}`}><X className="w-4 h-4" /></Button>}
        <Button size="sm" variant="ghost" onClick={() => del(r.id)} data-testid={`tst-del-${r.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </div>
    </CardContent></Card>
  );
  return (
    <div className="space-y-4">
      {pending.length > 0 && <div className="space-y-2" data-testid="tst-pending">
        <div className="font-semibold text-amber-800">{t("tst_awaiting")} ({pending.length})</div>
        {pending.map((r) => <Row key={r.id} r={r} />)}
      </div>}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card><CardContent className="p-6 space-y-3">
          <div className="font-semibold">{t("add_testimonial")}</div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("name")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tst-name" /></div>
            <div><Label>{t("city")}</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="tst-city" /></div>
          </div>
          <div><Label>{t("testimonial_text")}</Label><Textarea value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="tst-text" /></div>
          <div><Label>Rating</Label><Input type="number" min={1} max={5} value={f.rating} onChange={(e) => setF({ ...f, rating: +e.target.value })} className="w-24" data-testid="tst-rating" /></div>
          <Button onClick={add} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tst-add"><Plus className="w-4 h-4 mr-1" />{t("add_testimonial")}</Button>
          <p className="text-xs text-slate-500">{t("review_hint")}</p>
        </CardContent></Card>
        <div className="space-y-3">{others.map((r) => <Row key={r.id} r={r} />)}</div>
      </div>
    </div>
  );
}
